import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import cityExplorerService from '../../services/cityExplorerService';

const SHAREABLE_KINDS = ['presentation', 'documentation', 'calculator'];
const EMPTY_FORM = {
  label: '',
  categoryIds: [],
  resourceIds: [],
  access: 'public',
  password: '',
};

const shareableResources = (location) =>
  (location?.resources || []).filter((resource) => SHAREABLE_KINDS.includes(resource.kind));

const formHasSelection = (form) => form.categoryIds.length > 0 || form.resourceIds.length > 0;

const formFromShare = (share) => ({
  label: share.label || '',
  categoryIds: [...(share.categoryIds || [])],
  resourceIds: [...(share.resourceIds || [])],
  access: share.access === 'password' ? 'password' : 'public',
  password: '',
});

export const absoluteShareUrl = (path) => {
  if (typeof window === 'undefined') return path;
  return `${window.location.origin}${path}`;
};

const toggleId = (ids, id, checked) =>
  checked ? [...ids, id] : ids.filter((item) => item !== id);

// Opportunity and risk maps/calculators are flagged `sensitive` in
// location.json; the backend refuses them on public links.
export const sensitiveIds = (location) =>
  new Set([
    ...(location?.categories || []).filter((item) => item.sensitive).map((item) => item.id),
    ...(location?.resources || []).filter((item) => item.sensitive).map((item) => item.id),
  ]);

const selectionIsSensitive = (form, sensitive) =>
  [...form.categoryIds, ...form.resourceIds].some((id) => sensitive.has(id));

const ItemLabel = ({ label, sensitive }) =>
  sensitive ? (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
      {label}
      <Box
        component="span"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, color: 'text.secondary', fontSize: '0.75rem' }}
      >
        <LockOutlinedIcon sx={{ fontSize: 14 }} aria-hidden />
        Password only
      </Box>
    </Box>
  ) : (
    label
  );

const ShareLinksDialog = ({ open, onClose, location, initialResourceIds = [] }) => {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('list');
  const [editingShare, setEditingShare] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [copiedId, setCopiedId] = useState(null);

  const locationId = location?.id;
  const categories = location?.categories || [];
  const resources = useMemo(() => shareableResources(location), [location]);
  const sensitive = useMemo(() => sensitiveIds(location), [location]);
  const needsPassword = selectionIsSensitive(form, sensitive);

  const toggleItem = (field, id, checked) =>
    setForm((prev) => ({
      ...prev,
      [field]: toggleId(prev[field], id, checked),
      access: checked && sensitive.has(id) ? 'password' : prev.access,
    }));

  const loadShares = useCallback(async () => {
    if (!locationId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await cityExplorerService.listShares(locationId);
      setShares(data.shares || []);
    } catch (err) {
      setError(err.message || 'Failed to load share links');
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  const preselectKey = (initialResourceIds || []).join(',');

  useEffect(() => {
    if (!open) return undefined;
    const ids = preselectKey ? preselectKey.split(',') : [];
    setMode(ids.length ? 'form' : 'list');
    setEditingShare(null);
    const preselectSensitive = ids.some((id) => sensitive.has(id));
    setForm({ ...EMPTY_FORM, resourceIds: ids, access: preselectSensitive ? 'password' : 'public' });
    setCopiedId(null);
    loadShares();
    return undefined;
    // `sensitive` follows `location`; re-running for it would reset the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loadShares, preselectKey]);

  const startCreate = () => {
    setEditingShare(null);
    setForm(EMPTY_FORM);
    setError(null);
    setMode('form');
  };

  const startEdit = (share) => {
    setEditingShare(share);
    setForm(formFromShare(share));
    setError(null);
    setMode('form');
  };

  const copyLink = async (share) => {
    const url = absoluteShareUrl(share.path);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(share.id);
    } catch (_err) {
      setError('Could not copy the link. Select and copy it from the address below.');
    }
  };

  const saveForm = async () => {
    if (!formHasSelection(form)) {
      setError('Select at least one map, presentation, documentation file, or calculator');
      return;
    }
    if (needsPassword && form.access !== 'password') {
      setError('Opportunity and risk items can only be shared with a password');
      return;
    }
    if (form.access === 'password' && !editingShare && !form.password) {
      setError('A password is required');
      return;
    }
    if (form.access === 'password' && form.password && form.password.length < 4) {
      setError('Password must be at least 4 characters');
      return;
    }
    if (form.access === 'password' && editingShare?.access !== 'password' && !form.password) {
      setError('A password is required');
      return;
    }

    setSaving(true);
    setError(null);
    const body = {
      label: form.label.trim(),
      categoryIds: form.categoryIds,
      resourceIds: form.resourceIds,
      access: form.access,
    };
    if (form.access === 'password' && form.password) {
      body.password = form.password;
    }

    try {
      const data = editingShare
        ? await cityExplorerService.updateShare(editingShare.id, body)
        : await cityExplorerService.createShare(locationId, body);
      const saved = data.share;
      setShares((prev) => {
        const without = prev.filter((item) => item.id !== saved.id);
        return [saved, ...without];
      });
      setEditingShare(saved);
      setForm({ ...formFromShare(saved), password: '' });
      setMode('form');
    } catch (err) {
      setError(err.message || 'Failed to save share link');
    } finally {
      setSaving(false);
    }
  };

  const revokeShare = async (share) => {
    const confirmed = window.confirm(
      'Revoke this link? Anyone with it will lose access.'
    );
    if (!confirmed) return;
    setSaving(true);
    setError(null);
    try {
      await cityExplorerService.revokeShare(share.id);
      setShares((prev) => prev.filter((item) => item.id !== share.id));
      if (editingShare?.id === share.id) {
        setMode('list');
        setEditingShare(null);
      }
    } catch (err) {
      setError(err.message || 'Failed to revoke share link');
    } finally {
      setSaving(false);
    }
  };

  const canSave =
    formHasSelection(form) &&
    !(needsPassword && form.access !== 'password') &&
    !(form.access === 'password' && !editingShare && !form.password) &&
    !(form.access === 'password' && editingShare?.access !== 'password' && !form.password);

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      data-testid="share-links-dialog"
    >
      <DialogTitle>{mode === 'form' ? (editingShare ? 'Edit share link' : 'New share link') : 'Share maps'}</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {mode === 'list' && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Create a public or password-protected link with only the maps, files, and
              calculators you choose. Recipients will not see the rest of City Explorer.
            </Typography>
            <Typography variant="body2" color="text.secondary" data-testid="share-two-link-hint">
              Create one public link and one password link to split what&rsquo;s shared.
              {sensitive.size > 0 && ' Opportunity and risk items can only go on a password link.'}
            </Typography>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : shares.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No share links yet.
              </Typography>
            ) : (
              <Stack spacing={1.5} data-testid="share-links-list">
                {shares.map((share) => (
                  <Box
                    key={share.id}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      p: 1.5,
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" noWrap>
                          {share.label || 'Untitled share'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                          {absoluteShareUrl(share.path)}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={share.access === 'password' ? 'Password' : 'Public'}
                      />
                    </Stack>
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end" sx={{ mt: 1 }}>
                      <Tooltip title={copiedId === share.id ? 'Copied' : 'Copy link'}>
                        <IconButton
                          size="small"
                          onClick={() => copyLink(share)}
                          aria-label={`Copy link for ${share.label || 'Untitled share'}`}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Button size="small" onClick={() => startEdit(share)} sx={{ textTransform: 'none' }}>
                        Edit
                      </Button>
                      <Tooltip title="Revoke">
                        <IconButton
                          size="small"
                          onClick={() => revokeShare(share)}
                          aria-label={`Revoke ${share.label || 'Untitled share'}`}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </Stack>
        )}

        {mode === 'form' && (
          <Stack spacing={2}>
            {editingShare?.path && (
              <Alert
                severity="success"
                action={
                  <Button color="inherit" size="small" onClick={() => copyLink(editingShare)}>
                    {copiedId === editingShare.id ? 'Copied' : 'Copy'}
                  </Button>
                }
              >
                {absoluteShareUrl(editingShare.path)}
              </Alert>
            )}

            <TextField
              label="Name (optional)"
              value={form.label}
              onChange={(event) => setForm((prev) => ({ ...prev, label: event.target.value }))}
              fullWidth
              size="small"
            />

            <Box>
              <FormLabel component="legend">Maps</FormLabel>
              <Stack>
                {categories.map((category) => (
                  <FormControlLabel
                    key={category.id}
                    control={
                      <Checkbox
                        checked={form.categoryIds.includes(category.id)}
                        onChange={(event) => toggleItem('categoryIds', category.id, event.target.checked)}
                      />
                    }
                    label={<ItemLabel label={category.label} sensitive={sensitive.has(category.id)} />}
                  />
                ))}
              </Stack>
            </Box>

            {resources.length > 0 && (
              <Box>
                <FormLabel component="legend">Files and tools</FormLabel>
                <Stack>
                  {resources.map((resource) => (
                    <FormControlLabel
                      key={resource.id}
                      control={
                        <Checkbox
                          checked={form.resourceIds.includes(resource.id)}
                          onChange={(event) => toggleItem('resourceIds', resource.id, event.target.checked)}
                        />
                      }
                      label={<ItemLabel label={resource.label} sensitive={sensitive.has(resource.id)} />}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            <FormControl>
              <FormLabel>Who can open this link</FormLabel>
              <RadioGroup
                value={form.access}
                onChange={(event) => setForm((prev) => ({ ...prev, access: event.target.value }))}
              >
                <FormControlLabel
                  value="public"
                  control={<Radio />}
                  label="Anyone with the link"
                  disabled={needsPassword}
                />
                <FormControlLabel value="password" control={<Radio />} label="Password protected" />
              </RadioGroup>
              {needsPassword && (
                <FormHelperText sx={{ mx: 0 }}>
                  This link includes opportunity or risk items, so it needs a password.
                </FormHelperText>
              )}
            </FormControl>

            {form.access === 'password' && (
              <TextField
                label={editingShare?.access === 'password' ? 'New password (leave blank to keep)' : 'Password'}
                type="password"
                value={form.password}
                onChange={(event) => setForm((prev) => ({ ...prev, password: event.target.value }))}
                fullWidth
                size="small"
                autoComplete="new-password"
              />
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {mode === 'form' ? (
          <>
            <Button onClick={() => { setMode('list'); setError(null); }} disabled={saving} sx={{ mr: 'auto' }}>
              Back
            </Button>
            <Button onClick={onClose} disabled={saving}>
              Close
            </Button>
            <Button variant="contained" onClick={saveForm} disabled={saving || !canSave}>
              {saving ? 'Saving…' : editingShare ? 'Save changes' : 'Create link'}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose}>Close</Button>
            <Button variant="contained" onClick={startCreate} disabled={loading}>
              New link
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ShareLinksDialog;
